import React, { use } from "react";
import { useAtom, useSetAtom } from "jotai";
import ScreenCard from "../ScreenCard";
import { lastValidatedWorkingTime, newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import MissionEarningType from "../../components/editors/MissionEarningType";
import { EarningType, Repetition, Unit } from "../../__generated__/graphql";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { DEFAULT_HOURS_PER_WEEK } from "../../utils";
import {
    MAX_HOURS_PER_WEEK_FULL_TIME,
    setContractWorkingTimeClassicSpec,
} from "../../components/working_time/helpers";
import { isOnDemandWorkingTime } from "../../utils";

const CLEAN_WORKING_TIME_CLASSIC_PACKAGE = {
    repetition: Repetition.None,
    unit: Unit.Hours,
    unitCount: null,
    dailyHours: null,
};

const CreateMissionEarningType = () => {
    const i18n = useI18n();
    const [mission, setMission] = useAtom(newMissionState);
    const { validateScreen } = use(CreateMissionScreensContext);
    const setLastValidatedHoursPerWeekFullTime = useSetAtom(lastValidatedWorkingTime);
    const setEarningType = React.useCallback(
        (earningType: EarningType) => {
            setMission((oldMission) => {
                if (oldMission.currentContract?.earningType === earningType) {
                    return oldMission;
                }
                if (oldMission.clientInfo?.isPrivateClient) {
                    // for private client CCT is defined before the selection of earning
                    // type so we juste change the earning type. On demand is hourly only, so
                    // its spec is cleared on any other earning type.
                    const keepsWorkingTime =
                        earningType === EarningType.Hourly ||
                        !isOnDemandWorkingTime(oldMission.currentContract?.workingTimeClassicSpec);
                    return {
                        ...oldMission,
                        currentContract: {
                            ...(keepsWorkingTime
                                ? oldMission.currentContract
                                : setContractWorkingTimeClassicSpec(
                                      oldMission.currentContract ?? {},
                                      undefined,
                                  )),
                            earningType,
                        },
                    };
                } else {
                    // for non private client we reset CCT and working time so everything will be
                    // set correctly when user will choose CCT

                    // for monthly mission, we do not use max but the default so when user choose
                    // percentage it done on the default and not the max. It is like that because now percentage is defined before
                    // If we don't do that mission percentage of the mission may be wrong as hours per week will not match CCT standard hours
                    // mission percentage is important for salary compute (impot source, etc...)
                    // For non monthly we can use the max so user will be able to set up to the max. Later when setting the CCT we will
                    // adapt the hoursPerWeekFulltime to have correct percentage
                    const newHoursPerWeek =
                        earningType === EarningType.Monthly
                            ? DEFAULT_HOURS_PER_WEEK
                            : MAX_HOURS_PER_WEEK_FULL_TIME;

                    // for package mission we reset the workingTimeClassicSpec to a clean package spec.
                    // For other we just clean it so user will have set it
                    const newWorkingSpec =
                        earningType === EarningType.Package
                            ? CLEAN_WORKING_TIME_CLASSIC_PACKAGE
                            : undefined;

                    setLastValidatedHoursPerWeekFullTime({
                        hoursPerWeekFullTime:
                            oldMission.currentContract?.hoursPerWeekFullTime ?? undefined,
                        workingSpec: newWorkingSpec,
                    });
                    return {
                        ...oldMission,
                        currentContract: {
                            ...setContractWorkingTimeClassicSpec(
                                oldMission.currentContract ?? {},
                                newWorkingSpec,
                            ),
                            earningType,
                            hoursPerWeekFullTime: newHoursPerWeek,
                            cctEditionId: undefined,
                            cctContractId: undefined,
                            cctDetails: { readableValues: [], tempdataCallValues: [] },
                        },
                    };
                }
            });
        },
        [setMission, setLastValidatedHoursPerWeekFullTime],
    );

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionEarningType");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionEarningType");
    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: !!mission.currentContract?.earningType,
                },
            ]}
            withAssistanceButton
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_earning_type.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                <MissionEarningType
                    mission={mission}
                    setEarningType={setEarningType}
                ></MissionEarningType>
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionEarningType;
